import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

type Check = {
  ok: boolean;
  ms: number;
  attempts: number;
  error?: string;
  timedOut?: boolean;
};

type HealthBody = {
  status: "ok" | "degraded";
  uptimeMs: number;
  budgetMs: number;
  probeTimeoutMs: number;
  retryBackoffMs: number;
  checks: Record<string, Check>;
  at: string;
};

type Snapshot = {
  httpStatus: number;
  latencyMs: number;
  fetchedAt: number;
  body?: HealthBody;
  error?: string;
};

export const Route = createFileRoute("/health")({
  head: () => ({ meta: [{ title: "Health · System status" }] }),
  component: HealthDashboard,
});

const POLL_MS = 5_000;

function HealthDashboard() {
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [history, setHistory] = useState<Snapshot[]>([]);
  const [auto, setAuto] = useState(true);

  async function poll() {
    const t0 = performance.now();
    try {
      const res = await fetch("/api/public/health", { cache: "no-store" });
      const body = (await res.json()) as HealthBody;
      const next: Snapshot = {
        httpStatus: res.status,
        latencyMs: Math.round(performance.now() - t0),
        fetchedAt: Date.now(),
        body,
      };
      setSnap(next);
      setHistory((h) => [next, ...h].slice(0, 20));
    } catch (err) {
      const next: Snapshot = {
        httpStatus: 0,
        latencyMs: Math.round(performance.now() - t0),
        fetchedAt: Date.now(),
        error: err instanceof Error ? err.message : String(err),
      };
      setSnap(next);
      setHistory((h) => [next, ...h].slice(0, 20));
    }
  }

  useEffect(() => {
    poll();
    if (!auto) return;
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [auto]);

  const ok = snap?.body?.status === "ok";
  const statusColor = !snap ? "#64748b" : ok ? "#16a34a" : "#dc2626";
  const statusLabel = !snap ? "Loading…" : snap.error ? "UNREACHABLE" : snap.body?.status?.toUpperCase();

  return (
    <main style={{ fontFamily: "system-ui, sans-serif", maxWidth: 880, margin: "0 auto", padding: 24, color: "#0f172a" }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28 }}>System Health</h1>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 14 }}>
            Polling <code>/api/public/health</code> every {POLL_MS / 1000}s
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={poll} style={btnStyle}>Refresh</button>
          <button onClick={() => setAuto((v) => !v)} style={{ ...btnStyle, background: auto ? "#0f172a" : "#fff", color: auto ? "#fff" : "#0f172a" }}>
            {auto ? "Auto: ON" : "Auto: OFF"}
          </button>
        </div>
      </header>

      <section style={{ ...card, borderLeft: `6px solid ${statusColor}`, marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ width: 14, height: 14, borderRadius: "50%", background: statusColor, display: "inline-block" }} />
          <strong style={{ fontSize: 22 }}>{statusLabel}</strong>
          {snap && (
            <span style={{ marginLeft: "auto", color: "#64748b", fontSize: 13 }}>
              HTTP {snap.httpStatus || "—"} · {snap.latencyMs}ms · {new Date(snap.fetchedAt).toLocaleTimeString()}
            </span>
          )}
        </div>
        {snap?.error && <p style={{ color: "#dc2626", marginTop: 12 }}>{snap.error}</p>}
      </section>

      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, marginBottom: 12, color: "#475569", textTransform: "uppercase", letterSpacing: 0.6 }}>Dependencies</h2>
        {snap?.body ? (
          <div style={{ display: "grid", gap: 12 }}>
            {Object.entries(snap.body.checks).map(([name, c]) => (
              <div key={name} style={{ ...card, display: "flex", alignItems: "center", gap: 16 }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: c.ok ? "#16a34a" : "#dc2626" }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{name}</div>
                  {c.error && <div style={{ color: "#dc2626", fontSize: 13, marginTop: 2 }}>{c.error}</div>}
                </div>
                <div style={{ textAlign: "right", fontSize: 13, color: "#64748b" }}>
                  <div><strong style={{ color: "#0f172a" }}>{c.ms}ms</strong></div>
                  <div>attempts: {c.attempts}{c.timedOut ? " · timeout" : ""}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={card}>No data yet.</div>
        )}
      </section>

      <section>
        <h2 style={{ fontSize: 16, marginBottom: 12, color: "#475569", textTransform: "uppercase", letterSpacing: 0.6 }}>Recent polls</h2>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {history.map((h, i) => {
            const color = h.body?.status === "ok" ? "#16a34a" : h.error ? "#475569" : "#dc2626";
            return (
              <span
                key={i}
                title={`${new Date(h.fetchedAt).toLocaleTimeString()} · HTTP ${h.httpStatus} · ${h.latencyMs}ms`}
                style={{ width: 16, height: 28, background: color, borderRadius: 2, display: "inline-block" }}
              />
            );
          })}
          {history.length === 0 && <span style={{ color: "#64748b", fontSize: 14 }}>No polls yet.</span>}
        </div>
      </section>
    </main>
  );
}

const card: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  padding: 16,
  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
};

const btnStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  padding: "8px 14px",
  borderRadius: 8,
  fontSize: 14,
  cursor: "pointer",
};
